import * as duckdb from '@duckdb/duckdb-wasm';
import { useCallback, useEffect, useState } from 'react';
import { downloadURI } from '../../utils/downloadURI';
import { useDuckDb } from '../third-party-kit/hooks/useDuckDb';
import { getTempFilename } from '../third-party-kit/util/tempfile';

/**
 *
 * @param source Table name if exporting the whole table, or a query in parentheses
 * (see https://duckdb.org/docs/stable/guides/file_formats/csv_export.html)
 * @param fileNameWithoutExtension file name (.csv is appended)
 * @param shouldUpdate db update trigger
 * @param lazy if true, the file will not be loaded until the loadFile callback is called
 * @returns file ready indicator, callback for downloading the specified data as a .csv file, and a callback for loading the file if lazy is true
 */
export const useDownloadCsv = (
  source: string,
  fileNameWithoutExtension: string,
  shouldUpdate: boolean,
  lazy = false
): { fileReady: boolean; downloadFile: () => void; loadFile: () => void } => {
  const { db } = useDuckDb();
  const [blob, setBlob] = useState<Blob>();
  const [fileReady, setFileReady] = useState(false);
  const [shouldLoadFile, setShouldLoadFile] = useState(!lazy);

  useEffect(() => {
    let isCurrent = true;

    const tempFileName = getTempFilename();

    const createExportFile = async (db: duckdb.AsyncDuckDB) => {
      const conn = await db.connect();

      // Unnecessary warning log: https://github.com/duckdb/duckdb-wasm/issues/1842
      await conn.query(
        `COPY ${source} TO '${tempFileName}.csv' (HEADER, DELIMITER ',');`
      );

      const buffer = await db.copyFileToBuffer(`${tempFileName}.csv`);
      conn.close();

      if (isCurrent) {
        setBlob(new Blob([buffer]));
        setFileReady(true);
      }
    };

    if (shouldLoadFile && db && source && shouldUpdate) {
      setFileReady(false);
      createExportFile(db);
    }

    return () => {
      isCurrent = false;
      db?.dropFile(`${tempFileName}.csv`);
    };
  }, [source, shouldUpdate, db, shouldLoadFile]);

  const downloadFile = useCallback(() => {
    if (fileReady && blob) {
      downloadURI(URL.createObjectURL(blob), `${fileNameWithoutExtension}.csv`);
      if (lazy) {
        setShouldLoadFile(false);
      }
    }
  }, [fileReady, blob, fileNameWithoutExtension, lazy]);

  return {
    fileReady,
    downloadFile,
    loadFile: lazy ? () => setShouldLoadFile(true) : () => {},
  };
};
